import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { s, MdmShell, Field, ErrorAlert } from './_mdmKit.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { RecordPicker } from '../../components/RecordPicker.js';

/** M05 · C3 — Master Data Quality Dashboard.
 * Wires the frozen `/master/data-quality` contract:
 *   GET  `/entity-types` → { data: string[] } — the types a check can run on
 *   GET  `/entities`     ?entityType → { data: {entityId,code,name}[] }
 *   POST `/run`     {entityType,entityId,trigger?,snapshot?} → DqReport
 *   GET  `/latest`  ?entityType&entityId → T073 | null
 *   GET  `/report`  ?entityType&entityId&from&to&page&limit → {data,total,page,limit} */

const TRIGGERS = ['MANUAL', 'SAVE', 'IMPORT', 'SCHEDULED'] as const;
const PAGE_SIZE = 20;

interface DqEntity { entityId: string; code: string; name: string }
const entityLabel = (e: DqEntity): string => `${e.code} — ${e.name}`;
const entitiesOf = (type: string): string =>
  type ? `/master/data-quality/entities?entityType=${encodeURIComponent(type)}` : '';

interface DqCheck { checkCode: string; passed?: boolean; message: string }
interface DqReport {
  entityType: string;
  entityId: string;
  qualityScore: number;
  totalChecks: number;
  passingChecks: number;
  failingChecks: DqCheck[];
}
interface DqLog {
  dqLogId: string;
  entityType: string;
  entityId: string;
  /** Named by the server from the record's master (G18). */
  entityCode: string | null;
  entityName: string | null;
  qualityScore: number;
  totalChecks: number;
  passingChecks: number;
  failingCheckDetails: DqCheck[];
  checkTriggeredBy: string;
  checkedAt: string;
}

function errText(e: unknown): string {
  if (e && typeof e === 'object') {
    const body = (e as { body?: unknown }).body;
    if (body && typeof body === 'object' && 'message' in body) {
      const m = (body as { message?: unknown }).message;
      if (typeof m === 'string') return m;
      if (Array.isArray(m)) return m.join(', ');
    }
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return 'Request failed. Please try again.';
}

function scoreColor(score: number): string {
  if (score >= 90) return 'var(--color-success)';
  if (score >= 70) return 'var(--color-warning)';
  return 'var(--color-danger)';
}

export function DataQualityDashboardPage() {
  const { number } = useBusinessNumber();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();

  // ── Run-check panel ──
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [runEntityType, setRunEntityType] = useState<string>('');
  const [runEntityId, setRunEntityId] = useState<string>('');
  const [trigger, setTrigger] = useState<string>('MANUAL');
  const [report, setReport] = useState<DqReport | null>(null);
  const [latest, setLatest] = useState<DqLog | null | 'none'>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  // ── Report table ──
  const [logs, setLogs] = useState<DqLog[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listError, setListError] = useState<string | null>(null);
  const [fEntityType, setFEntityType] = useState<string>('');
  const [fEntityId, setFEntityId] = useState<string>('');
  const [fFrom, setFFrom] = useState<string>('');
  const [fTo, setFTo] = useState<string>('');

  const loadReport = useCallback(
    async (toPage = 1) => {
      setLogs(null);
      setListError(null);
      const qs = new URLSearchParams();
      if (fEntityType) qs.set('entityType', fEntityType);
      if (fEntityId.trim()) qs.set('entityId', fEntityId.trim());
      if (fFrom) qs.set('from', new Date(fFrom).toISOString());
      if (fTo) qs.set('to', new Date(fTo).toISOString());
      qs.set('page', String(toPage));
      qs.set('limit', String(PAGE_SIZE));
      try {
        const res = await apiRequest<{ data: DqLog[]; total: number; page: number }>(
          `/master/data-quality/report?${qs.toString()}`,
          { token },
        );
        setLogs(res.data);
        setTotal(res.total);
        setPage(res.page);
      } catch (err) {
        setListError(errText(err));
        setLogs([]);
      }
    },
    [fEntityType, fEntityId, fFrom, fTo, token],
  );

  useEffect(() => {
    void loadReport(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: string[] }>('/master/data-quality/entity-types', { token })
      .then((r) => {
        if (!alive) return;
        setEntityTypes(r.data);
        setRunEntityType((cur) => cur || (r.data[0] ?? ''));
      })
      .catch((err) => { if (alive) setRunError(errText(err)); });
    return () => { alive = false; };
  }, [token]);

  const runCheck = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setRunError(null);
      setReport(null);
      setLatest(null);
      if (!runEntityType || !runEntityId.trim()) {
        setRunError(t('mdm.dq.runValidation'));
        return;
      }
      setRunning(true);
      try {
        const res = await apiRequest<DqReport>('/master/data-quality/run', {
          method: 'POST',
          token,
          body: { entityType: runEntityType, entityId: runEntityId.trim(), trigger },
        });
        setReport(res);
        void loadReport(1);
      } catch (err) {
        setRunError(errText(err));
      } finally {
        setRunning(false);
      }
    },
    [runEntityType, runEntityId, trigger, token, loadReport],
  );

  const loadLatest = useCallback(async () => {
    setRunError(null);
    setLatest(null);
    if (!runEntityType || !runEntityId.trim()) {
      setRunError(t('mdm.dq.runValidation'));
      return;
    }
    try {
      const res = await apiRequest<DqLog | null>(
        `/master/data-quality/latest?entityType=${encodeURIComponent(runEntityType)}&entityId=${encodeURIComponent(runEntityId.trim())}`,
        { token },
      );
      setLatest(res ?? 'none');
    } catch (err) {
      setRunError(errText(err));
    }
  }, [runEntityType, runEntityId, token]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <MdmShell
      label="Master data quality dashboard"
      title={t('mdm.dq.title')}
      description={t('mdm.dq.desc')}
      backTo="/admin/mdm/lookups"
      backLabel={t('mdm.common.backLabel')}
    >
      {/* ── Run a check ── */}
      <form aria-label="Run data quality check" onSubmit={runCheck} style={{ ...s.card, marginBottom: 20 }}>
        {runError && <ErrorAlert message={runError} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
          <Field id="dq-entity-type" label={t('mdm.dq.entityType')}>
            <select id="dq-entity-type" style={s.input} value={runEntityType} onChange={(e) => { setRunEntityType(e.target.value); setRunEntityId(''); }}>
              {entityTypes.map((et) => (<option key={et} value={et}>{et}</option>))}
            </select>
          </Field>
          <RecordPicker<DqEntity>
            endpoint={entitiesOf(runEntityType)}
            idField="entityId"
            optionLabel={entityLabel}
            value={runEntityId}
            onPick={(id) => setRunEntityId(id)}
            label={t('mdm.dq.entityId')}
            testId="dq-entity-id"
          />
          <Field id="dq-trigger" label={t('mdm.dq.trigger')}>
            <select id="dq-trigger" style={s.input} value={trigger} onChange={(e) => setTrigger(e.target.value)}>
              {TRIGGERS.map((tr) => (<option key={tr} value={tr}>{tr}</option>))}
            </select>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Button type="submit" data-testid="run-check" variant="primary" disabled={running}>
            {running ? t('mdm.dq.running') : t('mdm.dq.runBtn')}
          </Button>
          <Button type="button" data-testid="load-latest" variant="secondary" onClick={loadLatest}>
            {t('mdm.dq.loadLatestBtn')}
          </Button>
        </div>
      </form>

      {report && (
        <div data-testid="dq-result" style={{ ...s.card, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <span data-testid="dq-score" style={{ fontSize: 34, fontWeight: 700, color: scoreColor(report.qualityScore) }}>
              {number(report.qualityScore)}%
            </span>
            <span style={{ color: s.muted }}>{t('mdm.dq.checksPassed', { passing: report.passingChecks, total: report.totalChecks })}</span>
          </div>
          {report.failingChecks.length === 0 ? (
            <p data-testid="dq-all-pass" style={{ color: 'var(--color-success)', margin: '10px 0 0' }}>{t('mdm.dq.allPass')}</p>
          ) : (
            <ul data-testid="dq-failing-list" style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              {report.failingChecks.map((c) => (
                <li key={c.checkCode}><strong>{c.checkCode}</strong> — {c.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {latest === 'none' && (
        <p data-testid="dq-latest-none" style={{ color: s.muted, marginBottom: 20 }}>{t('mdm.dq.latestNone')}</p>
      )}
      {latest && latest !== 'none' && (
        <div data-testid="dq-latest" style={{ ...s.card, marginBottom: 20 }}>
          <p style={{ margin: 0 }}>
            <strong>{t('mdm.dq.latestScoreLabel')}</strong>{' '}
            <span style={{ color: scoreColor(latest.qualityScore) }}>{number(latest.qualityScore)}%</span>{' '}
            ({latest.passingChecks}/{latest.totalChecks}) · {latest.checkTriggeredBy} · {latest.checkedAt ? at(latest.checkedAt) : '—'}
          </p>
        </div>
      )}

      {/* ── Report ── */}
      <h2 style={{ fontSize: 16, margin: '0 0 10px' }}>{t('mdm.dq.qualityReport')}</h2>
      <form
        aria-label="Filter quality report"
        onSubmit={(e) => { e.preventDefault(); void loadReport(1); }}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 14 }}
      >
        <Field id="report-entity-type" label={t('mdm.dq.filterEntityType')}>
          <select id="report-entity-type" style={s.input} value={fEntityType} onChange={(e) => { setFEntityType(e.target.value); setFEntityId(''); }}>
            <option value="">{t('mdm.common.all')}</option>
            {entityTypes.map((et) => (<option key={et} value={et}>{et}</option>))}
          </select>
        </Field>
        <RecordPicker<DqEntity>
          endpoint={entitiesOf(fEntityType)}
          idField="entityId"
          optionLabel={entityLabel}
          value={fEntityId}
          onPick={(id) => setFEntityId(id)}
          label={t('mdm.dq.filterEntityId')}
          testId="report-entity-id"
          emptyText={fEntityType ? undefined : t('mdm.dq.pickTypeFirst')}
        />
        <Field id="report-from" label={t('mdm.dq.from')}>
          <DateInput id="report-from" type="date" style={s.input} value={fFrom} onValueChange={(nextValue) => setFFrom(nextValue)} />
        </Field>
        <Field id="report-to" label={t('mdm.dq.to')}>
          <DateInput id="report-to" type="date" style={s.input} value={fTo} onValueChange={(nextValue) => setFTo(nextValue)} />
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <Button type="submit" data-testid="apply-report" variant="secondary">{t('mdm.dq.apply')}</Button>
        </div>
      </form>

      {listError && <ErrorAlert message={listError} />}
      {logs === null ? (
        <p>{t('mdm.dq.loadingReport')}</p>
      ) : logs.length === 0 ? (
        <p data-testid="report-empty" style={{ color: s.muted }}>{t('mdm.dq.reportEmpty')}</p>
      ) : (
        <>
          <DataTable<DqLog>
            ariaLabel="Quality report"
            testId="report-table"
            rows={logs}
            rowKey={(l) => l.dqLogId}
            rowTestId={(l) => `report-row-${l.dqLogId}`}
            columns={[
              { key: 'entity', header: t('mdm.dq.colEntity'), render: (l) => l.entityType },
              { key: 'entityId', header: t('mdm.dq.colEntityId'), render: (l) => (l.entityCode ? `${l.entityCode} — ${l.entityName ?? ''}` : '—') },
              { key: 'score', header: t('mdm.dq.colScore'), render: (l) => <span style={{ color: scoreColor(l.qualityScore), fontWeight: 600 }}>{number(l.qualityScore)}%</span> },
              { key: 'passed', header: t('mdm.dq.colPassed'), render: (l) => `${l.passingChecks}/${l.totalChecks}` },
              { key: 'trigger', header: t('mdm.dq.colTrigger'), render: (l) => l.checkTriggeredBy },
              { key: 'checkedAt', header: t('mdm.dq.colCheckedAt'), render: (l) => (l.checkedAt ? at(l.checkedAt) : '—') },
              { key: 'failing', header: t('mdm.dq.colFailing'), render: (l) => l.failingCheckDetails?.length ?? 0 },
            ]}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <Button type="button" data-testid="report-prev" variant="secondary" disabled={page <= 1} onClick={() => void loadReport(page - 1)}>{t('mdm.dq.previous')}</Button>
            <span data-testid="report-page" style={{ color: s.muted }}>{t('mdm.dq.reportPage', { page, totalPages })}</span>
            <Button type="button" data-testid="report-next" variant="secondary" disabled={page >= totalPages} onClick={() => void loadReport(page + 1)}>{t('mdm.dq.next')}</Button>
          </div>
        </>
      )}
    </MdmShell>
  );
}

export default DataQualityDashboardPage;
